import { useState } from 'react'
import { Control, useController } from 'react-hook-form'
import { FormControl, FormItem, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import InputFieldContainer from '@/components/shared/inputComponents/InputFieldContainer'
import { useLanguage } from '@/context/LanguageContext'
import CtaTargetSelect, { CtaTargetType } from './CtaTargetSelect'

type CtaLinkMode = 'external' | 'internal'

export interface CtaEditorProps {
  /**
   * react-hook-form `control` for whichever form owns the fields below
   * (ContentBlock forms and the Page settings form both use this component,
   * so it stays loosely typed like the rest of the shared input components —
   * see `InputFieldContainer`'s `control: any`).
   */
  control: Control<any>
  /** Currently selected language index (matches the app's `useLanguage()`). */
  languageId: number
  /** Field path for the per-language CTA label, e.g. `page_profiles.0.cta_label`. */
  labelName: string
  /** Field path for the per-language external URL, e.g. `page_profiles.0.cta_link`. */
  linkName: string
  /** Field path for the top-level (non-translatable) "open in new tab" boolean. */
  newTabName: string
  /** Field path for the top-level (non-translatable) internal page id. */
  linkPageIdName: string
  /**
   * Field path for the top-level internal-target discriminator
   * (`"cms_page" | "category" | "store"`), mirroring the backend's
   * `cta_link_type`.
   */
  linkTypeName: string
  /**
   * Field path for the top-level internal category id (only meaningful when
   * the target type is `"category"`), mirroring the backend's
   * `cta_link_category_id`.
   */
  linkCategoryIdName: string
  /**
   * Field path for the top-level internal collection id (only meaningful
   * when the target type is `"collection"`), mirroring the backend's
   * `cta_link_collection_id`. Optional — when omitted (the default for every
   * existing caller), the Collections group is not fetched or rendered and
   * this component's behavior is byte-identical to before this prop existed.
   */
  linkCollectionIdName?: string
  /** A page id to exclude from the internal page picker (e.g. the page being edited). */
  pageIdToExclude?: string
}

/**
 * Reusable translatable CTA (label + link) editor, shared by the page hero
 * settings and the Text-on-Image content block. Edits ONE CTA for the
 * currently-selected language; the label/URL are per-language, while the
 * "open in new tab" flag and internal target (CMS page / category / the
 * store) are top-level (shared across languages) — mirroring the backend's
 * `CtaLinkInput` + profile split.
 *
 * The External/Internal toggle uses real component state, NOT the
 * '' / 'select' sentinel-string hack the existing button link config
 * overloads `link_page_id` with.
 */
export default function CtaEditor({
  control,
  labelName,
  linkName,
  newTabName,
  linkPageIdName,
  linkTypeName,
  linkCategoryIdName,
  linkCollectionIdName,
  pageIdToExclude,
}: CtaEditorProps) {
  const { language } = useLanguage()

  const { field: linkTypeField } = useController({
    control,
    name: linkTypeName,
  })

  const { field: linkPageIdField } = useController({
    control,
    name: linkPageIdName,
  })

  const { field: linkCategoryIdField } = useController({
    control,
    name: linkCategoryIdName,
  })

  // `useController` needs a stable field path on every render, so when the
  // caller doesn't pass `linkCollectionIdName` this falls back to an unused
  // path that nothing else reads or submits — every write below is guarded
  // on `linkCollectionIdName` being set (see `handleModeChange`/
  // `handleTargetChange`) so a caller that omits the prop never has this
  // phantom field's value touched, keeping existing callers byte-identical.
  const { field: linkCollectionIdField } = useController({
    control,
    name: linkCollectionIdName ?? '__cta_editor_unused_collection_id',
  })

  const { field: linkField } = useController({
    control,
    name: linkName,
  })

  // Real mode state. Starts undefined (derived from whatever the internal
  // target fields already hold — internal iff a discriminator or legacy page
  // id is present), so it correctly reflects data that loads asynchronously
  // (edit mode) as long as the user hasn't manually toggled the radio yet.
  // Once the user picks a mode explicitly, that choice sticks regardless of
  // the fields' values.
  const [manualMode, setManualMode] = useState<CtaLinkMode | null>(null)
  const mode: CtaLinkMode =
    manualMode ??
    (linkTypeField.value || linkPageIdField.value ? 'internal' : 'external')

  const handleModeChange = (next: CtaLinkMode) => {
    setManualMode(next)
    if (next === 'external') {
      // Clear the internal target fields so a stale value can't be submitted
      // alongside an external URL.
      linkTypeField.onChange(null)
      linkPageIdField.onChange('')
      linkCategoryIdField.onChange(null)
      if (linkCollectionIdName) linkCollectionIdField.onChange(null)
    } else {
      // Clear the external URL so a stale value can't be submitted alongside
      // an internal target.
      linkField.onChange('')
    }
  }

  // Derive the CtaTargetSelect value from the underlying fields. Legacy rows
  // saved before `cta_link_type` existed only have `link_page_id` set, so
  // fall back to `cms_page` when a page id is present but the type isn't
  // (mirrors the portal's legacy-fallback resolution).
  const targetType: CtaTargetType | null =
    (linkTypeField.value as CtaTargetType | null) ??
    (linkPageIdField.value ? 'cms_page' : null)
  const targetId: string | number | null =
    targetType === 'category'
      ? linkCategoryIdField.value ?? null
      : targetType === 'collection'
        ? linkCollectionIdField.value ?? null
        : targetType === 'cms_page'
          ? linkPageIdField.value || null
          : null

  const handleTargetChange = (next: {
    type: CtaTargetType
    id: string | number | null
  }) => {
    linkTypeField.onChange(next.type)
    linkPageIdField.onChange(next.type === 'cms_page' ? next.id ?? '' : '')
    linkCategoryIdField.onChange(next.type === 'category' ? next.id : null)
    if (linkCollectionIdName) {
      linkCollectionIdField.onChange(next.type === 'collection' ? next.id : null)
    }
  }

  return (
    <>
      <InputFieldContainer
        label="CTA Label"
        description="Text shown on the button"
        control={control}
        name={labelName}
        render={({ field }) => (
          <FormItem className="col-span-3">
            <FormControl>
              <Input
                placeholder="e.g., Shop Now"
                type="text"
                {...field}
                value={field.value ?? ''}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <div className="grid grid-cols-4 items-start gap-4">
        <Label className="text-right pt-3">Link Type</Label>
        <div className="col-span-3 flex gap-4 pt-3">
          <label className="flex items-center gap-2">
            <input
              type="radio"
              checked={mode === 'external'}
              onChange={() => handleModeChange('external')}
            />
            <span className="text-sm">External URL</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              checked={mode === 'internal'}
              onChange={() => handleModeChange('internal')}
            />
            <span className="text-sm">Internal page</span>
          </label>
        </div>
      </div>

      {mode === 'external' ? (
        <>
          <InputFieldContainer
            label="CTA URL"
            control={control}
            name={linkName}
            render={({ field }: { field: React.InputHTMLAttributes<HTMLInputElement> }) => (
              <FormItem className="col-span-3">
                <FormControl>
                  <Input
                    placeholder="https://example.com"
                    type="text"
                    {...field}
                    value={(field.value as string) ?? ''}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <InputFieldContainer
            label="New Tab"
            control={control}
            name={newTabName}
            render={({ field }: { field: { value: boolean; onChange: (value: boolean) => void } }) => (
              <FormItem className="col-span-3">
                <FormControl>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                    <Label className="text-sm font-normal">Open in new tab</Label>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </>
      ) : (
        <div className="grid grid-cols-4 items-start gap-4">
          <Label className="text-right pt-3">CTA Target</Label>
          <div className="col-span-3">
            <CtaTargetSelect
              value={{ type: targetType, id: targetId }}
              onChange={handleTargetChange}
              pageIdToExclude={pageIdToExclude}
              language={language}
              includeCollections={Boolean(linkCollectionIdName)}
            />
          </div>
        </div>
      )}
    </>
  )
}
